import { useEffect, useState } from 'react';
import { useUsers } from '@/lib/queries';
import type { UserRow } from '@/lib/types';
import { Avatar } from '@/components/Avatar';
import { Modal } from '@/components/Modal';

interface Props {
  app: { key: string; label: string } | null;
  businessLine: string | null;
  onClose: () => void;
  onSelectUser: (upn: string) => void;
}

const PAGE_SIZE = 20;

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function Row({ u, onClick }: { u: UserRow; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid w-full grid-cols-[2rem_1fr_8rem_6rem] items-center gap-4 border-b border-ink-800/40 px-6 py-2 text-left transition-colors hover:bg-ink-900/60"
    >
      <Avatar upn={u.upn} fullName={u.full_name} size="sm" />
      <div className="min-w-0">
        <div className="truncate text-sm text-ink-100">{u.full_name}</div>
        <div className="truncate text-xs text-ink-500">{u.upn}</div>
      </div>
      <div className="text-xs text-ink-400">{u.business_line ?? '—'}</div>
      <div className="text-right text-xs tabular-nums text-ink-500">
        {fmtDate(u.last_activity_date)}
      </div>
    </button>
  );
}

export function AppUsersDrawer({ app, businessLine, onClose, onSelectUser }: Props) {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [app, businessLine]);

  const { data, isLoading, isFetching } = useUsers({
    page,
    pageSize: PAGE_SIZE,
    businessLine,
    onlyInactive: false,
    activeInApp: app?.key ?? null,
  });

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Modal
      open={app !== null}
      onClose={onClose}
      ariaLabel={app ? `Usuarios activos en ${app.label}` : ''}
      maxWidth="2xl"
    >
      <header className="border-b border-ink-800 px-8 py-5">
        <div className="label">Usuarios activos · 30d</div>
        <h2 className="mt-1 text-2xl font-light tracking-tight text-ink-50">
          {app?.label}
          {businessLine ? (
            <span className="ml-2 text-base text-ink-500">/ {businessLine}</span>
          ) : null}
        </h2>
        <div className="mt-1 text-xs text-ink-500">
          {isLoading ? '…' : `${total} usuarios contabilizados`}
        </div>
      </header>

      <div className="grid grid-cols-[2rem_1fr_8rem_6rem] gap-4 border-b border-ink-800/60 px-6 py-2 text-[0.65rem] uppercase tracking-widest text-ink-500">
        <span />
        <span>Nombre</span>
        <span>Filial</span>
        <span className="text-right">Última actividad</span>
      </div>

      <div className="max-h-[55vh] overflow-y-auto">
        {isLoading && items.length === 0 ? (
          <div className="grid h-40 place-items-center text-ink-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="grid h-40 place-items-center text-ink-500">Sin usuarios</div>
        ) : (
          items.map((u) => (
            <Row key={u.upn} u={u} onClick={() => onSelectUser(u.upn)} />
          ))
        )}
      </div>

      {totalPages > 1 ? (
        <footer className="flex items-center justify-between border-t border-ink-800 px-6 py-3 text-xs text-ink-500">
          <div>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage(page - 1)}
              className="px-2 py-1 text-ink-300 disabled:text-ink-700"
            >
              ← Anterior
            </button>
            <span className="tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage(page + 1)}
              className="px-2 py-1 text-ink-300 disabled:text-ink-700"
            >
              Siguiente →
            </button>
          </div>
        </footer>
      ) : null}
    </Modal>
  );
}
